/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { Message, MessageBubble } from '../../styled';
import { MIntegratedImagery } from '../MIntegratedImagery';
import { MCategoryList } from '../MCategoryList';
import { PropertyData, OpenHotelPanelOptions } from '../MIntegratedImagery/MIntegratedImagery.types';
import { isUserCancelledAssistantMessage } from '../../store';
import { MRedirectLink } from '../MRedirectLink';
import { MDefaultTemplate } from '../MDefaultTemplate';
import { useHqvMarkdownComponents } from '../../hooks/useHqvMarkdownComponents';
import { MErrorMessage } from '../MErrorMessage';
import { MResponseActions } from '../MResponseActions';

export type ChatThumbFeedback = 'up' | 'down';

export interface ChatMessageProps {
  message: {
    id: string;
    content?: string;
    userName?: string;
    isUser: boolean;
    timestamp: Date;
    toolsUsed?: string[];
    intent?: string;
    hasPreview?: boolean;
    previewData?: any;
    navigationButton?: any;
    recommendedPropertyIds?: string[];
    uiData?: any;
    uiType?: string;
    thumbFeedback?: ChatThumbFeedback;
  };
  chatId?: string | null;
  onNavigate?: (messageId: string) => void;
  onOpenHotelPanel?: (propertyData: PropertyData, propertyId: string, options?: OpenHotelPanelOptions) => void;
  onChipClick?: (chipText: string) => void;
  onViewAllProperties?: (viewData: any) => void;
  onRetry?: (originalMessage: string, errorMessageId: string) => void;
  onThumbFeedback?: (messageId: string, direction: ChatThumbFeedback) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  chatId,
  onOpenHotelPanel,
  onChipClick,
  onViewAllProperties,
  onRetry,
  onThumbFeedback,
}) => {
  // Pass message-specific viewData to ensure HQV links use correct prices for this message
  const hqvComponents = useHqvMarkdownComponents(onOpenHotelPanel, message.previewData?.data);

  const onThumbFeedbackForMessage =
    chatId && onThumbFeedback ? (direction: ChatThumbFeedback) => onThumbFeedback(message.id, direction) : undefined;

  /** Synthetic cancel row from store — hide thumbs (same rules as `isUserCancelledAssistantMessage`). */
  const suppressThumbsForUserCancelled = isUserCancelledAssistantMessage(message);

  const renderMessageContent = () => {
    if (message.isUser) {
      return message.content;
    }

    switch (message.uiType) {
      case 'integratedImagery':
        return (
          <MIntegratedImagery
            uiData={message.uiData}
            viewData={message.previewData?.data}
            onOpenHotelPanel={onOpenHotelPanel}
            onChipClick={onChipClick}
            onViewAllProperties={viewData => onViewAllProperties?.(viewData)}
            messageId={message.id}
            thumbFeedback={message.thumbFeedback}
            onThumbFeedback={onThumbFeedbackForMessage}
          />
        );
      case 'categoryList':
        return (
          <MCategoryList
            uiData={message.uiData}
            viewData={message.previewData?.data}
            onOpenHotelPanel={onOpenHotelPanel}
            onChipClick={onChipClick}
            onViewAllProperties={viewData => onViewAllProperties?.(viewData)}
            messageId={message.id}
            thumbFeedback={message.thumbFeedback}
            onThumbFeedback={onThumbFeedbackForMessage}
          />
        );
      case 'redirectBlock':
        return (
          <MRedirectLink
            uiData={message.uiData}
            onChipClick={onChipClick}
            messageId={message.id}
            thumbFeedback={message.thumbFeedback}
            onThumbFeedback={onThumbFeedbackForMessage}
          />
        );
      case 'error':
        return <MErrorMessage uiData={message.uiData} onRetry={onRetry} />;
      case 'guardrail_limit_exceeded':
        return <MErrorMessage uiData={{ errorText: message.content || '', originalMessage: '', errorMessageId: '' }} />;
      default:
        if (message.uiData) {
          return (
            <MDefaultTemplate
              uiData={message.uiData}
              viewData={message.previewData?.data}
              onChipClick={onChipClick}
              onOpenHotelPanel={onOpenHotelPanel}
              onViewAllProperties={viewData => onViewAllProperties?.(viewData)}
              messageId={message.id}
              thumbFeedback={message.thumbFeedback}
              onThumbFeedback={onThumbFeedbackForMessage}
            />
          );
        }
        return (
          <>
            <MarkdownPreview source={message.content || ''} components={hqvComponents} />
            {!suppressThumbsForUserCancelled ? (
              <MResponseActions
                messageId={message.id}
                thumbFeedback={message.thumbFeedback}
                onThumbsUp={
                  onThumbFeedbackForMessage
                    ? () => {
                        if (message.thumbFeedback) return;
                        onThumbFeedbackForMessage('up');
                      }
                    : undefined
                }
                onThumbsDown={
                  onThumbFeedbackForMessage
                    ? () => {
                        if (message.thumbFeedback) return;
                        onThumbFeedbackForMessage('down');
                      }
                    : undefined
                }
              />
            ) : null}
          </>
        );
    }
  };

  return (
    <Message isUser={message.isUser}>
      <MessageBubble isUser={message.isUser} isError={message.uiType === 'error'}>
        {renderMessageContent()}
      </MessageBubble>
    </Message>
  );
};
